import React from 'react';
import { Box } from '@mui/material';

interface SwipeTabsProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  trackRef: React.RefObject<HTMLDivElement | null>;
  numTabs: number;
  children: React.ReactNode;
  panelPb?: number | string;
}

/**
 * Pure visual wrapper for swipeable tab panels.
 *
 * Renders an overflow-hidden clip container and a flex track that slides
 * horizontally. All children remain mounted at all times.
 *
 * `touch-action: pan-y` on the container lets the browser handle vertical
 * scroll natively while horizontal gesture events are captured by useSwipeTabs.
 *
 * Wire up with the hook:
 *   const { containerRef, trackRef } = useSwipeTabs(tabIndex, numTabs, onChange, enabled);
 *   <SwipeTabs containerRef={containerRef} trackRef={trackRef} numTabs={n}>
 *     {panels}
 *   </SwipeTabs>
 */
export const SwipeTabs = React.memo(function SwipeTabs({
  containerRef,
  trackRef,
  numTabs,
  children,
  panelPb,
}: SwipeTabsProps) {
  const panelWidth = `${100 / numTabs}%`;

  return (
    <Box
      ref={containerRef}
      sx={{ overflow: 'hidden', width: '100%', touchAction: 'pan-y', flex: 1, minHeight: 0 }}
    >
      <Box
        ref={trackRef}
        sx={{
          display: 'flex',
          width: `${numTabs * 100}%`,
          willChange: 'transform',
          height: '100%',
        }}
      >
        {React.Children.map(children, (child, i) => (
          <Box
            key={i}
            sx={{ flex: `0 0 ${panelWidth}`, maxWidth: panelWidth, minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden', pt: 2, ...(panelPb !== undefined ? { pb: panelPb } : {}) }}
          >
            {child}
          </Box>
        ))}
      </Box>
    </Box>
  );
});
